import React from 'react';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
    return (
        <div className="pt-32 pb-24 relative overflow-hidden bg-white">
            <div className="absolute inset-0 bg-grid-pattern opacity-[0.15]"></div>

            <section className="max-w-4xl mx-auto px-6 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <p className="text-cyan-600 font-mono text-sm tracking-widest uppercase mb-4">Legal</p>
                    <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight">
                        Privacy Policy
                    </h1>
                    <p className="text-slate-500 text-sm mb-16">Last updated: April 4, 2026</p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="prose prose-slate max-w-none"
                >
                    <div className="space-y-12 text-slate-600 leading-relaxed">
                        <div>
                            <h2 className="text-xl font-bold text-slate-900 mb-4">1. Introduction</h2>
                            <p>
                                Aetrix Logic ("we," "our," or "us") is committed to protecting the privacy of our clients, partners, and visitors. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage our services.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-xl font-bold text-slate-900 mb-4">2. Information We Collect</h2>
                            <p className="mb-4">We may collect the following types of information:</p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li><strong>Personal Information:</strong> Name, email address, phone number, company name, and job title when you fill out our contact forms or book a call.</li>
                                <li><strong>Usage Data:</strong> Information about how you interact with our website, including pages visited, time spent, browser type, and device information.</li>
                                <li><strong>Cookies & Tracking:</strong> We use cookies and similar technologies to enhance your browsing experience and analyze site traffic.</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="text-xl font-bold text-slate-900 mb-4">3. How We Use Your Information</h2>
                            <p className="mb-4">We use the information we collect to:</p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Respond to your inquiries and provide requested services.</li>
                                <li>Communicate with you about projects, updates, and promotional offers.</li>
                                <li>Improve our website, services, and user experience.</li>
                                <li>Comply with legal obligations and protect our rights.</li>
                                <li>Analyze usage trends and optimize performance.</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="text-xl font-bold text-slate-900 mb-4">4. Data Sharing & Disclosure</h2>
                            <p className="mb-4">
                                We do not sell your personal information. We may share your data with:
                            </p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li><strong>Service Providers:</strong> Trusted third parties who assist us in operating our website and conducting our business (e.g., hosting, analytics).</li>
                                <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process.</li>
                                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="text-xl font-bold text-slate-900 mb-4">5. Data Security</h2>
                            <p>
                                We implement industry-standard security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-xl font-bold text-slate-900 mb-4">6. Data Retention</h2>
                            <p>
                                We retain personal information only for as long as necessary to fulfill the purposes for which it was collected, or as required by applicable laws and regulations.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-xl font-bold text-slate-900 mb-4">7. Your Rights</h2>
                            <p className="mb-4">Depending on your location, you may have the right to:</p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Access the personal data we hold about you.</li>
                                <li>Request correction of inaccurate data.</li>
                                <li>Request deletion of your personal data.</li>
                                <li>Object to or restrict certain processing activities.</li>
                                <li>Withdraw consent at any time where processing is based on consent.</li>
                            </ul>
                            <p className="mt-4">
                                To exercise any of these rights, please contact us at the details provided below.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-xl font-bold text-slate-900 mb-4">8. Third-Party Links</h2>
                            <p>
                                Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites. We encourage you to read the privacy policies of any external sites you visit.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-xl font-bold text-slate-900 mb-4">9. Changes to This Policy</h2>
                            <p>
                                We may update this Privacy Policy from time to time. Any changes will be posted on this page with a revised "Last updated" date. We encourage you to review this policy periodically.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-xl font-bold text-slate-900 mb-4">10. Contact Us</h2>
                            <p>
                                If you have any questions about this Privacy Policy, please contact us at:
                            </p>
                            <p className="mt-4 font-medium text-slate-900">
                                Aetrix Logic<br />
                                Vadodara, India & United Kingdom<br />
                                Email: contact@aetrixlogic.com
                            </p>
                        </div>
                    </div>
                </motion.div>
            </section>
        </div>
    );
};

export default PrivacyPolicy;
